// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { styledScrollbar } from "@bg/XDL/Utilities";
import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tippy, { TippyProps } from "@tippyjs/react";
import React from "react";
import styled, { withTheme } from "styled-components";
import { Instance } from "tippy.js";

export interface TooltipPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
        tooltipArrow?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement;
        /** The text that will appear in the tooltip */
        text?: string | React.ReactElement | React.ReactNode[];
        /** Function called on tooltip open */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Function called after tooltip opened */
        onOpened?: React.EventHandler<React.SyntheticEvent>;
        /** Function called after tooltip closed */
        onClosed?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
        /** Props that will be passed to the tooltip to determin how to pass the text of the tooltip
         * @themable */
        passAsChildren?: boolean;
    }
>;

export type TooltipProps = TooltipComponentProps & TooltipPresentationProps & TippyProps;

export const TooltipContainer = styled.div<InjectableCss<any>>`
    background-color: ${getColor("common.white")};
    padding: 20px;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    max-width: 300px;
    box-shadow: 0 0 25px 0 rgba(0, 0, 0, 0.18);

    &[data-placement^="top"] > .tippy-arrow {
        bottom: -5px;
        &:before {
            transform: rotate(45deg);
        }
    }

    &[data-placement^="bottom"] > .tippy-arrow {
        top: -5px;
        &:before {
            transform: rotate(-135deg);
        }
    }

    &[data-placement^="left"] > .tippy-arrow {
        right: -5px;
        &:before {
            transform: rotate(-45deg);
        }
    }

    &[data-placement^="right"] > .tippy-arrow {
        left: -5px;
        &:before {
            transform: rotate(135deg);
        }
    }

    .tippy-arrow,
    .tippy-arrow::before {
        position: absolute;
        width: 10px;
        height: 10px;
        z-index: 1;
    }

    .tippy-arrow::before {
        content: "";
        background: ${getColor("common.white")};
        box-shadow: 0 0 25px 0 rgba(0, 0, 0, 0.18);
    }
    ${styledScrollbar(8)}
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    ${injectCss}
`;

const TooltipTriggerer = styled.span.attrs({ tabIndex: 0 })<InjectableCss<any>>`
    &:focus {
        outline-offset: 1px;
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
    }
    ${injectCss}
`;

/**
 * Component that provides supplementatal information on click or enter.
 */

const Tooltip: React.FC<TooltipProps> = props => {
    const {
        text,
        triggerComponent,
        triggerAltText,
        iconProps,
        mergeCss,
        onOpen,
        onClose,
        children,
        passAsChildren,
        ...otherProps
    } = props;

    if (
        (passAsChildren && !children) ||
        (!passAsChildren && (!text || React.isValidElement(text) || (typeof text === "string" && text.length === 0)))
    ) {
        return null;
    }
    const { spreadProps } = applyPropBuilder(props, { component: "tooltip" });
    const { applyStyledProp } = applyPropBuilder(props, { component: "tooltip" });
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.tooltip?.defaultProps?.mergeCss;
    let trigger: {} | null | undefined;

    if (triggerComponent) {
        trigger = triggerComponent;
    } else {
        trigger = <IconMemo {...spreadProps("iconProps")} />;
    }

    const toolTipComponent =
        typeof text === "string" ? (
            <Typography as="p" {...spreadProps("typographyProps")}>
                {text}
            </Typography>
        ) : (
            text
        );

    return (
        <Tippy
            onShow={instance => {
                onOpen?.({ target: instance.reference, instance } as any);
                (window as any).tooltipInstance && (window as any).tooltipInstance?.hide();
                (window as any).tooltipInstance = instance;
            }}
            onHide={instance => {
                onClose?.({ target: instance.reference, instance } as any);
            }}
            render={attrs => (
                <TooltipContainer
                    css={applyStyledProp(["cssOverrides", "tooltipContainer"], resolvedMergeCss)}
                    {...attrs}
                >
                    <TooltipBody css={applyStyledProp(["cssOverrides", "tooltipBody"], resolvedMergeCss)}>
                        {passAsChildren ? children : toolTipComponent}
                    </TooltipBody>

                    <span data-popper-arrow="" className="tippy-arrow" />
                </TooltipContainer>
            )}
            offset={({ placement }) => {
                if (placement === "bottom-start" || placement === "top-start") {
                    return [-15, 15];
                }

                if (placement === "bottom-end" || placement === "top-end") {
                    return [15, -15];
                }

                return [15, 15];
            }}
            {...omitMultiple(otherProps, ["cssOverrides", "typographyProps"])}
        >
            <TooltipTriggerer
                css={applyStyledProp(["cssOverrides", "tooltipClickable"], resolvedMergeCss)}
                data-test-selector="tooltip_OpenTooltip"
            >
                {trigger}
                <VisuallyHidden>{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
            </TooltipTriggerer>
        </Tippy>
    );
};

Tooltip.defaultProps = {
    trigger: "mouseenter focus",
    placement: "bottom",
    interactive: true,
    animation: false,
    passAsChildren: false,
};

/** @component */
export default withTheme(Tooltip);
